import React from "react";
import PropTypes from "prop-types";
import Icon from "@material-ui/core/Icon";
import CardHeader from "components/Card/CardHeader.jsx";

/**
 * Standard page-level card header with title, subtitle, and action buttons.
 *
 * Usage:
 *   <PageCardHeader
 *     title="Departments"
 *     subtitle="4 departments"
 *     icon="business"          // optional material icon
 *     actions={[
 *       { label: "Add Department", icon: "add", onClick: fn },
 *       { label: "Export", icon: "download", onClick: fn, variant: "outline" }
 *     ]}
 *   />
 */
function PageCardHeader({ title, subtitle, icon, actions }) {
  return (
    <CardHeader style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px" }}>
      {/* Left: optional icon + title block */}
      <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
        {icon && (
          <div style={{
            width: "40px", height: "40px",
            borderRadius: "10px",
            background: "rgba(255,255,255,0.15)",
            backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0
          }}>
            <Icon style={{ color: "#fff", fontSize: "20px" }}>{icon}</Icon>
          </div>
        )}
        <div>
          <h4 style={{ margin: 0, color: "#fff", fontWeight: "700", fontSize: "16px", lineHeight: 1.3 }}>
            {title}
          </h4>
          {subtitle && (
            <p style={{ margin: "3px 0 0", color: "rgba(255,255,255,0.75)", fontSize: "12px", lineHeight: 1.4 }}>
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Right: action buttons */}
      {actions && actions.length > 0 && (
        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexShrink: 0 }}>
          {actions.map((action, i) => {
            const isPrimary = i === 0;
            const baseStyle = {
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              padding: "7px 16px",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: "600",
              letterSpacing: "0.3px",
              cursor: "pointer",
              border: "none",
              outline: "none",
              transition: "all 0.18s ease",
              lineHeight: 1.4
            };
            const primaryStyle = {
              ...baseStyle,
              background: "#ffffff",
              color: "#1a237e",
              boxShadow: "0 2px 8px rgba(0,0,0,0.18)"
            };
            const outlineStyle = {
              ...baseStyle,
              background: "rgba(255,255,255,0.12)",
              color: "#ffffff",
              border: "1px solid rgba(255,255,255,0.45)",
              backdropFilter: "blur(4px)"
            };
            return (
              <button
                key={i}
                onClick={action.onClick}
                disabled={action.disabled}
                style={isPrimary ? primaryStyle : outlineStyle}
                onMouseEnter={e => {
                  if (isPrimary) {
                    e.currentTarget.style.background = "#e8eaf6";
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.22)";
                  } else {
                    e.currentTarget.style.background = "rgba(255,255,255,0.22)";
                  }
                }}
                onMouseLeave={e => {
                  if (isPrimary) {
                    e.currentTarget.style.background = "#ffffff";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.18)";
                  } else {
                    e.currentTarget.style.background = "rgba(255,255,255,0.12)";
                  }
                }}
              >
                {action.icon && (
                  <Icon style={{ fontSize: "15px", lineHeight: 1 }}>{action.icon}</Icon>
                )}
                {action.label}
              </button>
            );
          })}
        </div>
      )}
    </CardHeader>
  );
}

PageCardHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.string,
  actions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    onClick: PropTypes.func,
    disabled: PropTypes.bool
  }))
};

export default PageCardHeader;
